import React, { useState, useContext, useEffect, useCallback } from 'react';
import {
  useWindowDimensions,
  useDirectTransCheck,
  useIECheck,
  isSchemaValid,
  sortErrors,
  scrollTop,
} from 'utils';
import AppContext from 'fluxes/context';
import { CHOICES_MAP, COMMENT_MAP, CHOICES_NAME_MAP } from 'utils/constant';
import { UPDATE_ADD_SKILL_INPUT, CHANGE_VALID } from 'fluxes/actions';

import PresentPC from 'components/pages/AddSkill/PresentPC';
import PresentSP from 'components/pages/AddSkill/PresentSP';

// チェックボックス項目
const CHECKBOX_ITEM_MAP = {
  // Excelスキル
  ExcelskillItem: {
    title: 'Excelスキル',
    label: '必須',
    infoStr: 'excelSkill',
    type: 'BasicCheckBoxInput',
  },
  // その他スキル
  OtherSkillsItem: {
    title: 'その他スキル',
    label: '任意',
    infoStr: 'otherskills',
    type: 'BasicCheckBoxInput',
  },
};

//------------------------------
const AddSkill = () => {
  // 基本設定
  const path = window.location.pathname;
  const { state, dispatch } = useContext(AppContext);
  // レスポンシブ
  const { width } = useWindowDimensions();
  const isIE = useIECheck();
  const isSp = width <= 640 && !isIE;

  // 変数設定
  const [excelSkill, setExcelSkill] = useState(state.excelSkill);
  const [otherSkills, setOtherSkills] = useState(state.otherSkills);
  const [skillOtherCmnt, setSkillOtherCmnt] = useState(state.skillOtherCmnt);

  // 直接遷移チェック
  useDirectTransCheck({ state });

  //------------------------------
  // 入力チェック ---------->>
  // 入力チェック用変数
  const initialValid = { valid: state.validMap, errorMessages: [] };
  const [valids, setValids] = useState({
    schema: initialValid, // 画面全体のチェック
  });

  // 初期入力チェック設定
  // 表示したいものだけ
  const [isShowError, setIsShowError] = useState({
    skillOtherCmnt: false,
  });

  // エラーの画面表示で利用する値
  const valid = Object.keys(valids).every((key) => valids[key].valid);
  const dispErrors = useCallback(() => {
    const { schema } = valids;
    const errors = sortErrors([...schema.errorMessages]);
    const errorMap = errors.reduce((obj, error) => {
      const { target, message } = error;
      if (!isShowError[target] || Object.keys(obj).includes(target)) return obj;
      return { ...obj, [target]: message };
    }, {});
    return errorMap;
  }, [isShowError, valids]);

  // 入力チェック（全体、現状はskillOtherCmntだけ）
  useEffect(() => {
    const [isValid, newErrorMessages] = isSchemaValid({
      target: {
        excelSkill,
        otherSkills,
        skillOtherCmnt,
      },
    });
    // erros更新
    const newValid = {
      schema: { valid: isValid, errorMessages: newErrorMessages },
    };
    setValids((org) => ({ ...org, ...newValid }));

  }, [
    excelSkill,
    otherSkills,
    skillOtherCmnt,
    valid
  ]);
  // 入力チェック <<----------

  //------------------------------
  // ボタン押下 ------------->>
  // state更新処理
  const updateInputState = () => {
    dispatch({
      type: UPDATE_ADD_SKILL_INPUT,
      updAddSkillInput: {
        excelSkill,
        otherSkills,
        skillOtherCmnt,
      },
    });
  };
  // 押下時の処理
  const onClickMainButton = {
    prev: () => {
      scrollTop();
      updateInputState();
      dispatch({
        type: CHANGE_VALID,
        path,
        valid,
      });
    },
    next: () => {
      scrollTop();
      updateInputState();
      dispatch({
        type: CHANGE_VALID,
        path,
        valid,
      });
    },
  };
  // ボタン押下 <<----------

  //------------------------------
  // onChange ---------->>
  // Excelスキル
  const excelSkillOnChange = (e) => {
    const val = e.target.value;
    // excelSkillの変更を更新
    const addItem = [...excelSkill, val];
    const removeItem = excelSkill.filter((item) => {
      return item !== val;
    });
    const newExcelSkillList = excelSkill.includes(val) ? removeItem : addItem;
    setExcelSkill(newExcelSkillList);
  };

  // その他スキル
  const otherSkillsOnChange = (e) => {
    const val = e.target.value;
    const addItem = [...otherSkills, val];
    const removeItem = otherSkills.filter((item) => {
      return item !== val;
    });
    if (otherSkills.includes(val)) {
      setOtherSkills(removeItem);
    } else {
      setOtherSkills(addItem);
    }
  };

  // その他、ご希望のスキルや経験のテキストエリア
  const skillOtherCmntOnChange = (event) => {
    const val = event.target.value;
    setSkillOtherCmnt(val);
    // エラー設定
    if (!isShowError.skillOtherCmnt) {
      setIsShowError({ ...isShowError, skillOtherCmnt: true });
    }
  };
  // onChange <<----------

  //------------------------------
  // その他、ご希望のスキルや経験のテキストエリアの設定
  // 'skillOtherCmntOnChange' より下になる
  const skillOtherCmntItem = {
    title: COMMENT_MAP.skillOtherCmnt.title,
    label: CHOICES_NAME_MAP.option,
    name: 'skillOtherCmnt',
    type: 'TextAreaInput',
    errorDispMessages: dispErrors().skillOtherCmnt,
    list: [
      {
        name: 'skillOtherCmnt',
        rows: 5,
        cols: 100,
        placeholder: COMMENT_MAP.skillOtherCmnt.placeholder,
        maxLength: 500,
        wordCount: '(500文字以下)',
        value: skillOtherCmnt,
        onInput: skillOtherCmntOnChange,
        onChange: skillOtherCmntOnChange,
        onCompositionEnd: skillOtherCmntOnChange,
        inValid: !!(dispErrors().skillOtherCmnt),
      },
    ],
  };

  //------------------------------
  // 画面表示 ---------->>
  // Excelスキル
  const ExcelskillDisp = {
    title: CHECKBOX_ITEM_MAP.ExcelskillItem.title,
    subTitle: '',
    label: CHECKBOX_ITEM_MAP.ExcelskillItem.label,
    list: CHOICES_MAP.excelskillList.map((elem) => {
      const { infoStr } = CHECKBOX_ITEM_MAP.ExcelskillItem.infoStr;
      return {
        id: infoStr + elem.value,
        value: elem.value,
        label: elem.label,
        number: isSp ? 2 : 3.1, // 分割数
        onChange: excelSkillOnChange,
        checked: excelSkill.includes(elem.value),
      };
    }),
    type: CHECKBOX_ITEM_MAP.ExcelskillItem.type,
  };

  // その他スキル
  const OtherSkillsDisp = {
    title: CHECKBOX_ITEM_MAP.OtherSkillsItem.title,
    subTitle: '',
    label: CHECKBOX_ITEM_MAP.OtherSkillsItem.label,
    list: CHOICES_MAP.otherSkillsList.map((elem) => {
      const { infoStr } = CHECKBOX_ITEM_MAP.OtherSkillsItem.infoStr;
      return {
        id: infoStr + elem.value,
        value: elem.value,
        label: elem.label,
        number: isSp ? 2 : 3.1, // 分割数
        onChange: otherSkillsOnChange,
        checked: otherSkills.includes(elem.value),
      };
    }),
    type: CHECKBOX_ITEM_MAP.OtherSkillsItem.type,
  };

  // その他 テキストエリア
  const skillOtherCmntDisp = {
    id: skillOtherCmntItem.infoStr,
    title: skillOtherCmntItem.title,
    label: skillOtherCmntItem.label,
    list: skillOtherCmntItem.list,
    type: skillOtherCmntItem.type,
    errorDispMessages: skillOtherCmntItem.errorDispMessages,
  };
  // 画面表示 <<----------

  //------------------------------
  // PCとSPの表示の分岐
  return isSp ? (
    <PresentSP
      onClickMainButton={onClickMainButton}
      valid={valid}
      ExcelskillDisp={ExcelskillDisp}
      OtherSkillsDisp={OtherSkillsDisp}
      skillOtherCmntDisp={skillOtherCmntDisp}
    />
  ) : (
    <PresentPC
      onClickMainButton={onClickMainButton}
      valid={valid}
      ExcelskillDisp={ExcelskillDisp}
      OtherSkillsDisp={OtherSkillsDisp}
      skillOtherCmntDisp={skillOtherCmntDisp}
    />
  );
};

export default AddSkill;
